import type { EntradasDoAno, EntradasDoMes, RealizadoDoAno } from "@base/contracts";
import { cn } from "@base/ui";
import { ChevronsRight } from "lucide-react";
import { Fragment, useState } from "react";
import type { FunilCalculado } from "../domain/calculo";
import { TIPO_DA_ENTRADA, formatarEntrada, lerEntrada } from "../domain/entrada";
import { LINHAS, ROTULO_DA_SECAO, atingido, somarRealizado, type LinhaDoFunil, type Secao } from "../domain/linhas";
import { NOME_DO_MES, formatarAtingido, formatarValor } from "./formatos";

const SECOES: readonly Secao[] = ["midia", "funil", "financeiro"];

/**
 * A grade do Funil Projetado, no formato da planilha: 12 meses + Total, e
 * cada indicador com Meta, Realizado e % atingido. Celula escura e editavel.
 */
export function GradeDoFunil({
  entradas,
  calculado,
  realizado,
  mesAtual,
  aoMudarMes,
}: {
  readonly entradas: EntradasDoAno;
  readonly calculado: FunilCalculado;
  readonly realizado: RealizadoDoAno | undefined;
  /** 1 a 12 quando o ano exibido e o atual; destaca a coluna. */
  readonly mesAtual: number | null;
  readonly aoMudarMes: (indice: number, campo: keyof EntradasDoMes, valor: number) => void;
}) {
  const totalRealizado = realizado ? somarRealizado(realizado.meses) : null;

  return (
    <div className="overflow-hidden rounded-3xl bg-card shadow-sm ring-1 ring-border/70">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[80rem] border-separate border-spacing-0 text-sm">
          <caption className="sr-only">Metas e realizado de cada mês</caption>
          <thead>
            <tr className="text-xs text-muted-foreground">
              <th scope="col" className="sticky left-0 z-20 w-60 bg-card px-5 py-3 text-left font-medium">
                Indicador
              </th>
              {NOME_DO_MES.map((nome, indice) => (
                <th
                  key={nome}
                  scope="col"
                  className={cn(
                    "px-2 py-3 text-right font-medium",
                    mesAtual === indice + 1 && "bg-accent/60 text-accent-foreground",
                  )}
                >
                  {nome}
                </th>
              ))}
              <th scope="col" className="bg-muted/60 px-4 py-3 text-right font-semibold text-foreground">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {SECOES.map((secao) => (
              <Fragment key={secao}>
                <tr>
                  <th
                    colSpan={14}
                    scope="colgroup"
                    className="sticky left-0 border-t bg-muted/40 px-5 py-2 text-left text-xs font-semibold tracking-[0.12em] text-muted-foreground uppercase"
                  >
                    {ROTULO_DA_SECAO[secao]}
                  </th>
                </tr>
                {LINHAS.filter((linha) => linha.secao === secao).map((linha) => (
                  <BlocoDaLinha
                    key={linha.id}
                    linha={linha}
                    entradas={entradas}
                    calculado={calculado}
                    realizado={realizado}
                    totalRealizado={totalRealizado}
                    mesAtual={mesAtual}
                    aoMudarMes={aoMudarMes}
                  />
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BlocoDaLinha({
  linha,
  entradas,
  calculado,
  realizado,
  totalRealizado,
  mesAtual,
  aoMudarMes,
}: {
  readonly linha: LinhaDoFunil;
  readonly entradas: EntradasDoAno;
  readonly calculado: FunilCalculado;
  readonly realizado: RealizadoDoAno | undefined;
  readonly totalRealizado: ReturnType<typeof somarRealizado> | null;
  readonly mesAtual: number | null;
  readonly aoMudarMes: (indice: number, campo: keyof EntradasDoMes, valor: number) => void;
}) {
  const metas = calculado.meses.map((mes) => linha.meta(mes));
  const metaTotal = linha.meta(calculado.total);
  const realizados = realizado ? realizado.meses.map((mes) => linha.realizado(mes)) : Array<null>(12).fill(null);
  const realizadoTotal = totalRealizado ? linha.realizado(totalRealizado) : null;
  const destaque = (indice: number) => mesAtual === indice + 1 && "bg-accent/30";

  return (
    <>
      <tr className="group/linha">
        <th scope="row" className="sticky left-0 z-10 w-60 border-t bg-card py-2 pr-3 pl-5 text-left align-middle font-normal">
          <span className="flex items-center justify-between gap-3">
            <span className="min-w-0">
              <span className="block text-sm font-semibold leading-snug text-foreground">{linha.rotulo}</span>
              <span className="block text-xs leading-snug text-muted-foreground">{linha.descricao}</span>
            </span>
            <SubRotulo>Meta</SubRotulo>
          </span>
        </th>
        {metas.map((meta, indice) => (
          <td key={indice} className={cn("border-t px-1.5 py-1.5 text-right", destaque(indice))}>
            {linha.entrada ? (
              <CelulaEditavel
                rotulo={`${linha.rotulo} em ${NOME_DO_MES[indice]}`}
                campo={linha.entrada}
                valor={entradas.meses[indice]?.[linha.entrada] ?? 0}
                herdado={(entradas.meses[indice]?.[linha.entrada] ?? 0) === 0 ? meta : null}
                formatoHerdado={linha.formato}
                aoMudar={(valor) => aoMudarMes(indice, linha.entrada as keyof EntradasDoMes, valor)}
                aoRepetir={
                  indice < 11
                    ? () => {
                        const valor = entradas.meses[indice]?.[linha.entrada as keyof EntradasDoMes] ?? 0;
                        for (let seguinte = indice + 1; seguinte < 12; seguinte += 1) {
                          aoMudarMes(seguinte, linha.entrada as keyof EntradasDoMes, valor);
                        }
                      }
                    : undefined
                }
              />
            ) : (
              <span className="block px-2 py-1.5 font-medium tabular-nums">{formatarValor(linha.formato, meta)}</span>
            )}
          </td>
        ))}
        <td className="border-t bg-muted/60 px-4 py-1.5 text-right font-semibold tabular-nums">
          {formatarValor(linha.formato, metaTotal)}
        </td>
      </tr>
      <tr>
        <th scope="row" className="sticky left-0 z-10 bg-card py-1 pr-3 text-right font-normal">
          <SubRotulo>Realizado</SubRotulo>
        </th>
        {realizados.map((valor, indice) => (
          <td key={indice} className={cn("px-3.5 py-1 text-right tabular-nums text-muted-foreground", destaque(indice))}>
            {formatarValor(linha.formato, valor)}
          </td>
        ))}
        <td className="bg-muted/60 px-4 py-1 text-right tabular-nums text-muted-foreground">
          {formatarValor(linha.formato, realizadoTotal)}
        </td>
      </tr>
      <tr>
        <th scope="row" className="sticky left-0 z-10 bg-card pt-0.5 pr-3 pb-3 text-right font-normal">
          <SubRotulo>% atingido</SubRotulo>
        </th>
        {realizados.map((valor, indice) => (
          <td key={indice} className={cn("px-3.5 pt-0.5 pb-3 text-right", destaque(indice))}>
            <Atingido linha={linha} valor={atingido(metas[indice] ?? null, valor)} />
          </td>
        ))}
        <td className="bg-muted/60 px-4 pt-0.5 pb-3 text-right">
          <Atingido linha={linha} valor={atingido(metaTotal, realizadoTotal)} />
        </td>
      </tr>
    </>
  );
}

function SubRotulo({ children }: { readonly children: string }) {
  return <span className="shrink-0 text-[11px] whitespace-nowrap text-muted-foreground">{children}</span>;
}

function Atingido({ linha, valor }: { readonly linha: LinhaDoFunil; readonly valor: number | null }) {
  if (valor === null) return <span className="text-xs text-muted-foreground/60">—</span>;
  const bom = linha.sentido === "maior-melhor" ? valor >= 1 : valor <= 1;
  const perto = linha.sentido === "maior-melhor" ? valor >= 0.8 : valor <= 1.2;
  return (
    <span
      className={cn(
        "inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums",
        bom ? "bg-success/12 text-success" : perto ? "bg-warning/15 text-warning-foreground" : "bg-destructive/10 text-destructive",
      )}
    >
      {formatarAtingido(valor)}
    </span>
  );
}

/**
 * Celula de entrada. Guarda o texto enquanto a pessoa digita e so confirma ao
 * sair do campo (ou Enter). Vazia mostra, apagado, o valor herdado do mes anterior.
 */
function CelulaEditavel({
  rotulo,
  campo,
  valor,
  herdado,
  formatoHerdado,
  aoMudar,
  aoRepetir,
}: {
  readonly rotulo: string;
  readonly campo: keyof EntradasDoMes;
  readonly valor: number;
  readonly herdado: number | null;
  readonly formatoHerdado: LinhaDoFunil["formato"];
  readonly aoMudar: (valor: number) => void;
  readonly aoRepetir: (() => void) | undefined;
}) {
  const tipo = TIPO_DA_ENTRADA[campo];
  const [texto, setTexto] = useState<string | null>(null);
  const invalido = texto !== null && lerEntrada(tipo, texto) === null;

  function confirmar() {
    if (texto === null) return;
    const lido = lerEntrada(tipo, texto);
    if (lido !== null) {
      aoMudar(lido);
      setTexto(null);
    }
  }

  return (
    <div className="group/celula relative">
      <input
        aria-label={rotulo}
        aria-invalid={invalido}
        inputMode="decimal"
        value={texto ?? formatarEntrada(tipo, valor)}
        placeholder={herdado !== null && herdado > 0 ? formatarValor(formatoHerdado, herdado) : "—"}
        onChange={(evento) => setTexto(evento.target.value)}
        onBlur={confirmar}
        onKeyDown={(evento) => {
          if (evento.key === "Enter") (evento.target as HTMLInputElement).blur();
          if (evento.key === "Escape") setTexto(null);
        }}
        className={cn(
          "h-9 w-full min-w-[5.5rem] rounded-lg bg-secondary px-2 text-right text-sm font-medium text-secondary-foreground tabular-nums outline-none",
          "placeholder:text-secondary-foreground/40 focus-visible:ring-2 focus-visible:ring-ice",
          "aria-invalid:ring-2 aria-invalid:ring-destructive",
          tipo === "porcentagem" && "pr-6",
        )}
      />
      {tipo === "porcentagem" ? (
        <span aria-hidden="true" className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 text-xs text-secondary-foreground/60">
          %
        </span>
      ) : null}
      {aoRepetir && valor > 0 ? (
        <button
          type="button"
          onClick={aoRepetir}
          title="Repetir nos meses seguintes"
          aria-label={`Repetir ${rotulo} nos meses seguintes`}
          className="absolute -top-2 -right-1 hidden size-5 place-items-center rounded-full bg-primary text-primary-foreground shadow group-hover/celula:grid focus-visible:grid"
        >
          <ChevronsRight className="size-3" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
